// src/components/EventDetails.js

import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Save,
  Cancel,
  OpenInNew,
} from '@mui/icons-material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import { useSnackbar } from 'notistack';
import { auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import LinkifiedText from './LinkifiedText';
import LoadingState from './LoadingState';
import dayjs from 'dayjs';


function EventDetails() {
  const { id } = useParams(); // Event ID from URL
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [emailsToAdd, setEmailsToAdd] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [userLists, setUserLists] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [expandedPanels, setExpandedPanels] = useState({});
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedItemForPurchase, setSelectedItemForPurchase] = useState(null);
  const [purchasePrice, setPurchasePrice] = useState('');
  const priceInputRef = useRef(null);
  const [giftProfileDialogOpen, setGiftProfileDialogOpen] = useState(false);
  const [viewingGiftProfile, setViewingGiftProfile] = useState(null);

  const [currentUser, loadingAuth] = useAuthState(auth);
  const currentUserId = currentUser?.uid;

  useEffect(() => {
    if (currentUserId) {
      fetchAllEventData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentUserId]);


  const fetchAllEventData = async (showLoader = false) => {
    if (showLoader) {
      setInitialLoading(true);
    }
    try {
      await Promise.all([
        fetchEventDetails(),
        fetchListsSharedWithEvent(),
        fetchSharedLists(),
        fetchUserLists(),
      ]);
    } finally {
      if (showLoader) {
        setInitialLoading(false);
      }
    }
  };

  const fetchEventDetails = async () => {
    try {
      const response = await api.get(`/events/${id}`);
      setEvent(response.data);
      setEditedName(response.data.name);
      setEditedDate(dayjs(response.data.date).format('YYYY-MM-DD'));
    } catch (error) {
      console.error('Error fetching event details:', error);
      enqueueSnackbar('Failed to fetch event details.', { variant: 'error' });
    }
  };

  const fetchListsSharedWithEvent = async () => {
    try {
      await api.get(`/events/${id}/lists`);
    } catch (error) {
      console.error('Error fetching lists shared with event:', error);
      enqueueSnackbar('Failed to fetch shared lists.', { variant: 'error' });
    }
  };

  // Fetch shared list items in the event
  const fetchSharedLists = async () => {
    try {
      const response = await api.get(`/events/${id}/lists/items`);
      const fetchedItems = response.data.items;

      // Map over the items to add purchasedByIds
      const itemsWithAdditionalData = fetchedItems.map((item) => ({
        ...item,
        purchasedByIds: item.purchasedBy?.map((user) => user.firebaseUID) || [], // Extract IDs or return an empty array
      }));


      setItems(itemsWithAdditionalData);
    } catch (error) {
      console.error('Error fetching shared lists:', error);
      enqueueSnackbar('Failed to fetch shared lists.', { variant: 'error' });
    }
  };

  // Fetch lists owned by or shared directly with the user
  const fetchUserLists = async () => {
    try {
      const response = await api.get('/lists');
      setUserLists(response.data);
    } catch (error) {
      console.error('Error fetching user lists:', error);
      enqueueSnackbar('Failed to fetch user lists.', { variant: 'error' });
    }
  };

  const handleViewGiftProfile = async (member) => {
    try {
      const res = await api.get(`/users/gift-profile?email=${encodeURIComponent(member.email)}`);
      setViewingGiftProfile({ name: res.data.name || member.email, giftProfile: res.data.giftProfile || {} });
      setGiftProfileDialogOpen(true);
    } catch (error) {
      console.error('Error fetching gift profile:', error);
      enqueueSnackbar('Could not load gift preferences.', { variant: 'error' });
    }
  };

  const recordPurchaseFromEventItem = async (item, price = '') => {
    try {
      await api.post('/purchases', {
        person: item.listOwner || item.listOwnerEmail || 'Unknown',
        itemName: item.shortDescription || 'Unnamed item',
        price: price,
        quantity: 1,
        purchaseDate: event.date || new Date().toISOString(),
        notes: '',
        eventId: id,
        sourceListId: item.listId,
        sourceItemId: item._id,
      });
    } catch (error) {
      console.warn('Failed to record purchase for tracking:', error);
    }
  };

  // Purchase an item
  const handlePurchaseItem = async (item) => {
    // Show price dialog first
    setSelectedItemForPurchase(item);
    setPurchasePrice('');
    setPriceDialogOpen(true);
  };

  // Confirm purchase with price
  const handleConfirmPurchase = async () => {
    if (!selectedItemForPurchase) return;

    try {
      await api.post(`/lists/${selectedItemForPurchase.listId}/items/${selectedItemForPurchase._id}/purchase`);
      enqueueSnackbar('Item marked as purchased.', { variant: 'success' });
      recordPurchaseFromEventItem(selectedItemForPurchase, purchasePrice);
      fetchSharedLists();
      setPriceDialogOpen(false);
      setSelectedItemForPurchase(null);
      setPurchasePrice('');
    } catch (error) {
      console.error('Error purchasing item:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to purchase item.',
        { variant: 'error' }
      );
    }
  };

  // Unpurchase an item
  const handleUnpurchaseItem = async (listId, itemId) => {
    try {
      await api.post(`/lists/${listId}/items/${itemId}/unpurchase`);
      await api.delete(`/purchases/by-source`, { params: { sourceItemId: itemId } });
      enqueueSnackbar('Purchase undone.', { variant: 'success' });
      fetchSharedLists();
    } catch (error) {
      console.error('Error undoing purchase:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to undo purchase.',
        { variant: 'error' }
      );
    }
  };

  // Handle edit toggle
  const handleEditDoubleClick = () => {
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    if (editedName.trim() === '') {
      enqueueSnackbar('Event name cannot be empty.', { variant: 'warning' });
      return;
    }
    try {
      const response = await api.put(`/events/${id}`, { name: editedName, date: editedDate });
      setEvent(response.data);
      setIsEditing(false);
      enqueueSnackbar('Event updated.', { variant: 'success' });
    } catch (error) {
      console.error('Error updating event:', error);
      enqueueSnackbar('Failed to update event.', { variant: 'error' });
    }
  };

  const handleEditCancel = () => {
    setEditedName(event.name);
    setEditedDate(dayjs(event.date).format('YYYY-MM-DD'));
    setIsEditing(false);
  };

  const handleAddMembers = async () => {
    const emailList = emailsToAdd
      .split(',')
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email !== '');

    if (emailList.length === 0) {
      enqueueSnackbar('Please enter at least one email.', {
        variant: 'warning',
      });
      return;
    }

    // Simple email format validation
    const invalidEmails = emailList.filter(
      (email) => !/\S+@\S+\.\S+/.test(email)
    );
    if (invalidEmails.length > 0) {
      enqueueSnackbar(`Invalid email(s): ${invalidEmails.join(', ')}`, {
        variant: 'warning',
      });
      return;
    }

    try {
      await api.post(`/events/${id}/members`, { emails: emailList });
      fetchEventDetails();
      fetchListsSharedWithEvent();
      setAddMemberDialogOpen(false);
      setEmailsToAdd('');
      enqueueSnackbar('Members added successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Error adding members:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to add members.',
        { variant: 'error' }
      );
    }
  };

  const handleRemoveMember = async (email) => {
    try {
      await api.delete(`/events/${id}/members`, { data: { email } });
      fetchEventDetails();
      fetchListsSharedWithEvent();
      enqueueSnackbar('Member removed successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Error removing member:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to remove member.',
        { variant: 'error' }
      );
    }
  };

  // Group items by listId
  const groupedItems = items.reduce((groups, item) => {
    const listId = item.listId;
    if (!groups[listId]) {
      groups[listId] = {
        listId: listId,
        listName: item.listName,
        listOwner: item.listOwner,
        listOwnerEmail: item.listOwnerEmail,
        listOwnerId: item.listOwnerId,
        items: [],
      };
    }
    groups[listId].items.push(item);
    return groups;
  }, {});

  // Create a set of user list IDs for quick lookup
  const userListIds = new Set(userLists.map((list) => list._id));
  const isListOwnedByUser = (group) =>
    group.listOwnerId === currentUserId;
  const groupedListArray = Object.values(groupedItems);
  const sharedListsFromOthers = groupedListArray.filter((group) => !isListOwnedByUser(group));
  const sharedListsOwnedByUser = groupedListArray.filter((group) => isListOwnedByUser(group));

  const renderListAccordion = (group) => {
    const listOwnedByUser = isListOwnedByUser(group);
    const visibleItems = group.items.filter((item) => {
      if (listOwnedByUser) {
        return true;
      }
      return (
        !showOnlyAvailable ||
        !item.purchasedByIds ||
        item.purchasedByIds.length === 0
      );
    });

    return (
      <Accordion
        key={group.listId}
        expanded={!!expandedPanels[group.listId]}
        onChange={handleTogglePanel(group.listId)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls={`panel-${group.listId}-content`}
          id={`panel-${group.listId}-header`}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={1}
          >
            <Typography
              variant="h6"
              sx={{
                wordBreak: 'break-word',
                pr: 1,
                fontFamily: '"Fraunces", Georgia, serif',
              }}
            >
              {group.listName} by {group.listOwner || group.listOwnerEmail}
            </Typography>
            {/* Icon to navigate to list if user owns it or it's shared with them via email */}
            {userListIds.has(group.listId) && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent accordion toggle
                  navigate(`/lists/${group.listId}`);
                }}
              >
                <OpenInNew />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {visibleItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No items to show.
            </Typography>
          ) : (
            visibleItems.map((item) => {
              const isOwner = item.listOwnerId === currentUserId;
              const hasPurchased = item.purchasedByIds
                ? item.purchasedByIds.includes(currentUserId)
                : false;
              return (
                <Paper
                  key={item._id}
                  sx={{
                    p: 1,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                  >
                    {/* Item Details */}
                    <Box flexGrow={1} minWidth={0}>
                      <Typography variant="body1" sx={{ wordBreak: 'break-word', pr: 1 }}>
                        <LinkifiedText text={item.shortDescription} />
                      </Typography>
                    </Box>
                    {/* Conditionally render status chip and buttons for non-owners */}
                    {!isOwner && (
                      <>
                        {/* Purchase/Unpurchase Button */}
                        {hasPurchased ? (
                          <IconButton
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              handleUnpurchaseItem(item.listId, item._id)
                            }
                            sx={{
                              color: 'error.main',
                              backgroundColor: 'rgba(225,29,72,0.08)',
                              '&:hover': { backgroundColor: 'rgba(225,29,72,0.16)' },
                            }}
                          >
                            <RemoveShoppingCartIcon />
                          </IconButton>
                        ) : item.purchasedByIds.length > 0 ? (
                          <Tooltip title="Purchased by someone else">
                            <span>
                              <IconButton
                                variant="contained"
                                color="default"
                                size="small"
                                disabled
                              >
                                <AddShoppingCartIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <IconButton
                            variant="contained"
                            size="small"
                            onClick={() => handlePurchaseItem(item)}
                            sx={{
                              color: 'secondary.main',
                              backgroundColor: 'rgba(45,106,79,0.08)',
                              '&:hover': { backgroundColor: 'rgba(45,106,79,0.16)' },
                            }}
                          >
                            <AddShoppingCartIcon />
                          </IconButton>
                        )}
                      </>
                    )}

                  </Box>
                </Paper>
              );
            })
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // Default expanded state: others expanded, user's shared lists collapsed
  useEffect(() => {
    const defaults = {};
    groupedListArray.forEach((group) => {
      defaults[group.listId] = !isListOwnedByUser(group);
    });
    setExpandedPanels(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, userLists, currentUserId]);

  const handleTogglePanel = (panelId) => (event, isExpanded) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panelId]: isExpanded,
    }));
  };

  const isLoadingState = loadingAuth || initialLoading;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 0 },
        py: 3,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      {isLoadingState ? (
        <LoadingState message="Loading event details..." minHeight={400} />
      ) : event ? (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            gap={1}
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignSelf="stretch"
          >
            {/* Editable Event Name and Date */}
            {isEditing ? (
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <TextField
                  label="Event Name"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  label="Date"
                  type="date"
                  value={editedDate}
                  onChange={(e) => setEditedDate(e.target.value)}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <IconButton onClick={handleEditSave} color="primary">
                  <Save />
                </IconButton>
                <IconButton onClick={handleEditCancel} color="secondary">
                  <Cancel />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <Typography
                  variant="h4"
                  onDoubleClick={handleEditDoubleClick}
                  sx={{
                    cursor: 'pointer',
                    wordBreak: 'break-word',
                    fontFamily: '"Fraunces", Georgia, serif',
                  }}
                >
                  {event.name}
                </Typography>
                <Typography
                  variant="subtitle1"
                  onDoubleClick={handleEditDoubleClick}
                  sx={{
                    cursor: 'pointer',
                    wordBreak: 'break-word',
                    color: 'primary.dark',
                    fontWeight: 600,
                  }}
                >
                  {dayjs(event.date).format('MMMM D, YYYY')}
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              onClick={() => setAddMemberDialogOpen(true)}
            >
              Share Event
            </Button>
          </Box>

          {/* Filter Options */}
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyAvailable}
                  onChange={() => setShowOnlyAvailable(!showOnlyAvailable)}
                />
              }
              label="Show only items that have not been purchased"
            />
          </FormGroup>

          {/* Items List */}
          <Box mt={4}>
            <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>
              Lists shared with this event
            </Typography>
            {items.length === 0 ? (
              <Typography>No items found.</Typography>
            ) : (
              <>
                {sharedListsFromOthers.length > 0 ? (
                  sharedListsFromOthers.map(renderListAccordion)
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No lists from other people are shared with this event.
                  </Typography>
                )}

                <Box mt={3}>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                    Your shared lists
                  </Typography>
                  {sharedListsOwnedByUser.length > 0 ? (
                    sharedListsOwnedByUser.map(renderListAccordion)
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No lists you own are shared with this event yet.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>

          {/* Dialog to add members */}
          <Dialog
            open={addMemberDialogOpen}
            onClose={() => setAddMemberDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Event Members</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1">Members:</Typography>
              <Box mt={2} display="flex" flexWrap="wrap">
                {event.members.map((member) => (
                  <Chip
                    key={member._id}
                    label={member.email}
                    onClick={() => handleViewGiftProfile(member)}
                    onDelete={() => handleRemoveMember(member.email)}
                    sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
                  />
                ))}
              </Box>
              <DialogTitle>Add Members to {event.name}</DialogTitle>
              <TextField
                label="Emails to Add"
                value={emailsToAdd}
                onChange={(e) => setEmailsToAdd(e.target.value)}
                variant="outlined"
                fullWidth
                placeholder="Enter emails separated by commas"
                multiline
                minRows={2}
                helperText="Separate multiple emails with commas. People you add to this event can view
                    all lists shared with the event and purchase items off the lists without the list owner being able to see."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddMembers} variant="contained">
                Add Members
              </Button>
            </DialogActions>
          </Dialog>

          {/* Gift Profile Dialog */}
          <Dialog
            open={giftProfileDialogOpen}
            onClose={() => setGiftProfileDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              {viewingGiftProfile?.name ? `${viewingGiftProfile.name}'s Gift Preferences` : 'Gift Preferences'}
            </DialogTitle>
            <DialogContent>
              {viewingGiftProfile && (() => {
                const gp = viewingGiftProfile.giftProfile;
                const fields = [
                  { label: 'Clothing size', value: gp.clothingSize },
                  { label: 'Shoe size', value: gp.shoeSize },
                  { label: 'Colour preferences', value: gp.colourPreferences },
                  { label: 'Brand preferences', value: gp.brandPreferences },
                  { label: 'Other notes', value: gp.otherNotes },
                ];
                const hasAny = fields.some((f) => f.value);
                if (!hasAny) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                      No gift preferences set yet.
                    </Typography>
                  );
                }
                return (
                  <Box sx={{ pt: 1 }}>
                    {fields.map(({ label, value }) =>
                      value ? (
                        <Box key={label} sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {label}
                          </Typography>
                          <Typography variant="body2">{value}</Typography>
                        </Box>
                      ) : null
                    )}
                  </Box>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGiftProfileDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Price Entry Dialog */}
          <Dialog
            open={priceDialogOpen}
            onClose={() => setPriceDialogOpen(false)}
            onEntered={() => {
              if (priceInputRef.current) {
                priceInputRef.current.focus();
              }
            }}
          >
            <DialogTitle>Record Purchase</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedItemForPurchase && `${selectedItemForPurchase.shortDescription}`}
                </Typography>
                <TextField
                  label="Price Paid (optional)"
                  type="number"
                  fullWidth
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                  inputRef={priceInputRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleConfirmPurchase();
                    }
                  }}
                  helperText="Enter the price you paid for this item"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPriceDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleConfirmPurchase} variant="contained">
                Confirm Purchase
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Typography>Loading event details...</Typography>
      )}
    </Box>
  );
}

export default EventDetails;
